import { useMemo } from 'react';

import { CodeBlock } from '@/lib/base/code';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { cx } from '@/utils/cx';

import type { CreateAutomationPayload } from '../../../lib/proposals/core/types';
import { isCreateAutomationPayload } from '../../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../../lib/proposals/registry';
import { ActionError, ProposalContainer } from '../shared';

// Friendly verb for each CRM event type; falls back to a de-prefixed form.
const EVENT_VERB: Record<string, string> = {
  ahoy_object_created: 'created',
  ahoy_property_updated: 'updated',
  ahoy_association_added: 'linked',
  ahoy_association_removed: 'unlinked',
  ahoy_object_deleted: 'deleted',
};

function prettyType(name: string): string {
  return name.replace(/^ahoy_/, '').replace(/_/g, ' ');
}

// "Runs when a contact is created." Empty when triggers are absent/malformed.
function runsWhenText(entities: string[], events: string[]): string {
  if (entities.length === 0 || events.length === 0) {
    return '';
  }
  const who = entities.map(prettyType).join(' / ');
  const verb = events
    .map(event => EVENT_VERB[event] ?? prettyType(event))
    .join(' / ');
  return `Runs when a ${who} is ${verb}.`;
}

interface Check {
  key: string;
  label: string;
}

// The agent writes a plain-language `label` per fixture (the natural-language
// equivalent of that example). We render the string as-is — no interpretation —
// falling back to the fixture name, then a generic label.
function toCheck(fixture: unknown, index: number): Check {
  const record =
    fixture && typeof fixture === 'object'
      ? (fixture as Record<string, unknown>)
      : {};
  const labelValue =
    typeof record.label === 'string' ? record.label.trim() : '';
  const nameValue = typeof record.name === 'string' ? record.name.trim() : '';
  const label = labelValue || nameValue || `Example ${index + 1}`;
  return { key: nameValue || label, label };
}

function CheckRow({ label }: { label: string }) {
  return (
    <li className='flex items-start gap-2 text-sm text-fg-secondary'>
      <span
        aria-hidden='true'
        className='mt-0.5 shrink-0 text-[var(--color-text-success-primary)]'
      >
        ✓
      </span>
      <span>{label}</span>
    </li>
  );
}

export function CreateAutomationRenderer({
  action,
  className = '',
}: ActionRendererProps) {
  const { payload } = action;

  const derived = useMemo(() => {
    if (!isCreateAutomationPayload(payload)) {
      return null;
    }
    const p = payload as CreateAutomationPayload;
    const entities = Array.isArray(p.triggering_entity_types)
      ? p.triggering_entity_types
      : [];
    const events = Array.isArray(p.triggering_event_types)
      ? p.triggering_event_types
      : [];
    const fixtures = Array.isArray(p.fixtures) ? p.fixtures : [];
    const summary =
      (p.summary ?? '').trim() || (p.context_message ?? '').trim();
    return {
      displayName: p.display_name || 'Automation',
      summary,
      documentation: (p.documentation ?? '').trim(),
      runsWhen: runsWhenText(entities, events),
      checks: fixtures.map(toCheck),
      source: typeof p.source === 'string' ? p.source : '',
    };
  }, [payload]);

  if (derived === null) {
    return (
      <ActionError message='Invalid automation payload' className={className} />
    );
  }

  return (
    <ProposalContainer className={cx('px-4 py-3', className)}>
      <div>
        <div className='text-xs font-medium text-fg-tertiary'>Automation</div>
        <div className='text-base font-semibold text-fg-primary'>
          {derived.displayName}
        </div>
      </div>

      {derived.summary ? (
        <p className='text-sm text-fg-primary'>{derived.summary}</p>
      ) : null}

      {derived.runsWhen ? (
        <p className='text-xs text-fg-tertiary'>{derived.runsWhen}</p>
      ) : null}

      {derived.checks.length > 0 ? (
        <details className='text-xs'>
          <summary className='cursor-pointer text-fg-tertiary'>
            {derived.checks.length === 1
              ? 'Checked on this case'
              : `Checked on these ${derived.checks.length} cases`}
          </summary>
          <ul className='mt-2 flex flex-col gap-1.5'>
            {derived.checks.map(check => (
              <CheckRow key={check.key} label={check.label} />
            ))}
          </ul>
        </details>
      ) : null}

      {derived.documentation ? (
        <details className='text-xs'>
          <summary className='cursor-pointer text-fg-tertiary'>
            How it works
          </summary>
          <div className='mt-2 text-sm text-fg-secondary'>
            <MarkdownContent content={derived.documentation} size='compact' />
          </div>
        </details>
      ) : null}

      {derived.source ? (
        <details className='text-xs'>
          <summary className='cursor-pointer text-fg-tertiary'>
            See the script
          </summary>
          <CodeBlock
            source={derived.source}
            transparent
            className='mt-2 py-1 text-fg-secondary'
          />
        </details>
      ) : null}
    </ProposalContainer>
  );
}
